import React, {useState} from 'react';
import {
    Mail,
    BookOpen,
    Linkedin,
    Home,
    Microscope,
    GraduationCap,
    FileText,
    Lightbulb,
    ChevronDown,
    HeartHandshake
} from 'lucide-react';
import kurtImage from './assets/kurt-face-torso-crop.jpg';
import gdsVideo from './assets/2025-11-06_greater-dayton-school-video.mp4';

const App = () => {
    const [activeSection, setActiveSection] = useState('home');
    const [showContactGuidance, setShowContactGuidance] = useState(false);

    const sections = [
        {id: 'home', title: 'Home', icon: Home},
        {id: 'interests', title: 'Research', icon: Microscope},
        {id: 'researchGroup', title: 'Research Group', icon: GraduationCap},
        {id: 'publications', title: 'Publications', icon: FileText},
        {id: 'entrepreneurship', title: 'Entrepreneurship', icon: Lightbulb},
        {id: 'aiForGood', title: 'AI for Good', icon: HeartHandshake},
    ];

    const profileData = {
        name: "Kurt Keutzer",
        title: "Professor of the Graduate School, EECS, UC Berkeley",
        image: kurtImage,
        bio: "My approach to research has been driven by a couple of adages: \"Necessity is the mother of invention\" and \"Research has to be good for something before it is good for everything.\" Over my career the aim of my research has been to enable system designers to fully utilize the capabilities that silicon manufacturing has provided them.",
        contact: {
            address: "2121 Berkeley Way West, Room 8020",
        },
        researchFocus: [
            "Using what we've learned about parallel and distributed computing to speed up the training of Deep Neural Nets – e.g. FireCaffe",
            "Using what we've learned about embedded computing to design and implement fast, accurate, energy-efficient neural nets for computer vision problems – e.g. SqueezeNet, SqueezeDet, SqueezeSeg, SqueezeNext etc.",
            "Using what we've learned about mapping Deep Neural Nets to embedded hardware to explore the co-design of DNNs and NN accelerators – e.g. the Squeezelator"
        ],
        pastProjects: [
            "Par Lab",
            "Exploring Design Patterns for Parallel Computing",
            "Modern Embedded Systems Compilers Architectures and Languages (MESCAL)",
            "Closing the performance gap between ASIC and custom designs",
            "Closing the power gap between ASIC and custom designs",
            "Berkeley Advanced Chip Performance Calculator (BACPAC)",
            "Compilation of software for popular embedded processors - especially DSP's (eg. SPAM)"
        ],
        teachingHistory: [
            {
                course: "Engineering Parallel Software: CS194-15",
                semesters: ["Fall 2015", "Fall 2014", "Fall 2013", "Fall 2012", "Spring 2012", "Fall 2010"]
            },
            {
                course: "Computer-Aided Design of Integrated Circuits",
                semesters: ["Fall 2008", "2007", "2005", "2004", "2002", "2001", "2000", "1998"]
            },
            {
                course: "Software Engineering: CS169",
                semesters: ["Spring 2004", "Spring 2003", "Spring 2002", "Fall 2001", "Fall 2000"]
            },
            {
                course: "Introduction to the Entrepreneurial Business of Software, MOT290T/EECS290A",
                semesters: ["Fall 2006", "Fall 2003"]
            },
            {course: "Profiles of Entrepreneurship in EECS", semesters: ["Fall 2011"]},
            {course: "Patterns for Parallel Programming, CS294", semesters: ["Spring 2010", "2009", "2008"]},
            {course: "Advanced Computer Architecture, CS252", semesters: ["Fall 2000"]}
        ],
        students: {
            currentDoctoral: ["Peter Jin", "Bichen Wu", "Co-advising: Xiangyu Yue"],
            currentPostDoc: [
                "Amir Gholami, PhD, University of Texas",
                "Sicheng Zhao, PhD, Harbin Institute of Technology, Post Doctoral Fellow, Tsinghua University"
            ],
            currentVisitors: ["Liang Ma, Politecnico di Torino (Prof. Lavagno)"],
            graduatedDoctoral: [
                "Matthew Moskewicz (2017): Principal Engineer, DeepScale",
                "Forrest Iandola (2016): CEO, DeepScale",
                "Michael Anderson (2014): Research Staff, Intel Applications Research Lab",
                "David Sheffield (2013): Research Staff, Intel Microprocessor Group",
                "Ekaterina (Katya) Gonina (2013): Google Research",
                "Bor-Yiing Su (2012): Applied Research Scientist, Facebook",
                "Narayanan Sundaram (2012): Research Staff, Intel Applications Research Lab",
                "Bryan Catanzaro (2011): VP Applied Deep Learning Research, Nvidia",
                "Mark Murphy (2011): GoogleX → Google",
                "Jike Chong (2010): Chief Data Scientist, Acorns",
                "N. R. Satish (2009): Research Scientist, Facebook",
                "Kaushik Ravindran (2007): Research Staff, National Instruments",
                "William Plishker (2006): CEO, IGI Technologies",
                "David Chinnery (2006): R&D Manager, Mentor Graphics",
                "Andrew Mihal (2006): Engineer, Xperi Corporation",
                "Scott Weber (2005): SOC Design Engineer, Intel",
                "Niraj Shah (2004): VP Growth, Prosperworks",
                "Pinhong Chen (2003): R&D Group Director, Cadence Design Systems",
                "Dennis Sylvester: Professor, University of Michigan (actually Cheming Hu was his dissertation advisor, but I like to think of him as one of my own! Please excuse me. I can't help myself.)"
            ],
            formerPostdoc: [
                "Ashok Sudarsanam: Associate Vice President, MLA Software at SiMa.ai",
                "Khalid Ashraf: CEO Semion",
                "Chidamber Kulkarni: CTO rENIAC",
                "Dorothea Kolossa: Prof. Dr.-Ing., Ruhr-Universität Bochum",
                "Alberto Todeschini: Lecturer, University of California, Berkeley",
                "Matthias Gries: Principal Engineer, Huawei European Research Center, München",
                "Serdar Tasiran: Principal Software Engineer, Amazon Web Services",
                "Youngmin Yi: Assistant Professor, University of Seoul",
                "Michael Orshansky: Professor and John E. Kasch Faculty Fellow, ECE, Univ. Texas, Austin"
            ],
            formerVisiting: [
                "Kiseok Kwon, TPU Architect, Google",
                "Jeonjoo Lee, Systems LSI Lab, Samsung, Korea",
                "Younghoo Lee, Mobile Systems Lab, Samsung, Korea",
                "Seojin Kim, Mobile systems Lab, Samsung, Korea",
                "Masayuki Ito, Architect, Renesas",
                "Kees Vissers, Distinguished Engineer, Xilinx",
                "Christian Sauer, Principal, Cadence Design Systems"
            ]
        },
        researchGroup: {
            facultyAndPostdocs: [
                { name: "Kurt Keutzer", role: "Professor of the Graduate School" },
                { name: "Amir Gholami", role: "Assistant Researcher" },
                { name: "Wenzhao Zheng", role: "Post-doctoral Fellow" }
            ],
            phdStudents: [
                { name: "Nicholas Lee", role: "PhD Student (co-advised with Gopala Anumanchipalli)" },
                { name: "Haocheng Xi", role: "PhD Student" },
                { name: "Rishabh Tiwari", role: "PhD Student" },
                { name: "Yuezhou Hu", role: "PhD Student" },
                { name: "Harman Singh", role: "PhD Student" },
                { name: "Lutfi Eren Erdogan", role: "PhD Student" },
                { name: "Minseo Kim", role: "PhD Student" },
                { name: "Arnav Chavan", role: "PhD Student" }
            ],
            mastersAndGap: [
                { name: "Monishwaran Maheshwaran", role: "Masters Student" },
                { name: "Sebastian Zhao", role: "Gap" }
            ],
            recentPostdocAlumni: [
                { name: "Shanghang Zhang", role: "Assistant Professor, Beijing University" },
                { name: "Huanrui Yang", role: "Assistant Professor in ECE, University of Arizona" },
                { name: "Sicheng Zhao", role: "Research Associate Professor, Tsinghua University" },
                { name: "Dorothea Kolossa", role: "Professor of Electronic Systems of Medical Engineering, TU Berlin" }
            ],
            phdAlumni: [
                { name: "Coleman Hooper", role: "stealth startup" },
                { name: "Xiuyu Li", role: "stealth startup" },
                { name: "Chenfeng Xu", role: "Assistant Professor, University of Texas, Austin" },
                { name: "Sebastian Nehrdich", role: "Distinguished Assistant Professor, University of Tohoku, Sendai, Japan" },
                { name: "Sehoon Kim", role: "Member of Technical Staff, X.ai" },
                { name: "Zhen Dong", role: "Assistant Professor, University of California, Santa Barbara" },
                { name: "Colorado Reed", role: "Member of Technical Staff, Apple" },
                { name: "Sheng Shen", role: "Member of Technical Staff, X.ai" },
                { name: "Bichen Wu / 吴璧辰 / Wúbìchén", role: "Co-Founder at Stealth" },
                { name: "Peter Jin", role: "Senior Research Scientist, NVIDIA" },
                { name: "Matthew Moskewicz", role: "Founding Engineer, DeepScale" },
                { name: "Forrest Iandola", role: "AI Research Scientist, Meta (co-founder, CEO, at DeepScale)" },
                { name: "Michael Anderson", role: "Senior Deep Learning Architect, NVIDIA" },
                { name: "Ekaterina Gonina", role: "stealth mode startup" },
                { name: "David Sheffield", role: "Director of HW Engineering, NVIDIA" },
                { name: "Narayanan Sundaram", role: "Research Scientist, Meta" },
                { name: "Bor-Yiing Su", role: "Software Engineer, Meta" },
                { name: "Bryan Catanzaro", role: "Vice President, Applied Deep Learning Research, NVIDIA" },
                { name: "Jike Chong", role: "General Manager, Antidote Health" },
                { name: "Mark Murphy", role: "Staff Research Engineer, Google Deepmind" },
                { name: "N. R. Satish", role: "Research Scientist, Meta" },
                { name: "Kaushik Ravindran", role: "R&D Engineer, Meta" },
                { name: "William Plishker", role: "CTO, ACREW Imaging" },
                { name: "Andrew Mihal", role: "on sabbatical" },
                { name: "David Chinnery", role: "R&D Manager, Mentor Graphics" },
                { name: "Scott Weber", role: "Principal Engineer, Altera" },
                { name: "NIraj Shah", role: "Investor" },
                { name: "Pinhong Chen", role: "Fellow, Cadence Design Systems" }
            ]
        },
        press: {
            mesca: [
                "Press Response to the MESCAL Project, ASIPs, and Programmable Platforms",
                "Coverage of my Keynote Address at the Electronic Design Processes Workshop in 2006",
                "My position paper on programmable platforms in EE Times 30th Anniversary Special Issue",
                "MESCAL Reviewed in EE Times 30th Anniversary Special Issue",
                "EE Times interviews me on platforms",
                "EE Times interviews me on ASIPs",
                "EE Times focuses in on programmable ASICs and MESCAL",
                "EE Times discusses Nimble and it's relationship to MESCAL"
            ],
            timing: [
                "EE Times interviews me and (then postdoc) Orshansky on statistical timing",
                "EE Times interviews me on changing trends in timing analysis",
                "EE Times response to our (and other's talks) at Tau2002"
            ],
            gap: [
                "Richard Goering reviews our book Closing the Gap Between ASIC and Custom",
                "EE Times discussion of the DAC 2000 Session",
                "EE Times resports on my panel at DAC: Design Closure Hope or Hype?"
            ],
            submicron: [
                "EE Times resports Aristo's Incorporation of the ideas",
                "EE Times discusses the ISPD panel and its discussion of our ideas",
                "EE Times reports influence of our ideas at Cadence",
                "Goering's EE TIMES Article Covering the Debate at the Tau Conference",
                "Goering's EETIMES Front Page Article Covering the Original Talk (local copy)",
                "Gary Smith of Dataquest's Article (local copy)"
            ],
            misc: [
                "Joining the Coware Technical Advisory Board"
            ]
        },
        careerPath: "I took an unusual route to academia: I joined Berkeley's EECS faculty in January 1998 after fifteen years in industry. My last industry position was CTO and Senior Vice-President at Synopsys."
    };

    const getGradYearForName = (fullName) => {
        const entries = profileData.students.graduatedDoctoral || [];
        for (const entry of entries) {
            const match = entry.match(/^([^()]+)\s*\((\d{4})\)/);
            if (match) {
                const legacyName = match[1].trim();
                const year = match[2];
                if (fullName.startsWith(legacyName) || legacyName.startsWith(fullName)) {
                    return year;
                }
            }
        }
        return null;
    };

    const getMergedFormerPostdocs = () => {
        const seen = new Set();
        const merged = [];
        // Add recent postdoc alumni first
        (profileData.researchGroup.recentPostdocAlumni || []).forEach((p) => {
            const key = (p.name || '').trim();
            if (key && !seen.has(key)) {
                seen.add(key);
                merged.push(`${p.name}${p.role ? `: ${p.role}` : ''}`);
            }
        });
        // Then legacy former postdocs
        (profileData.students.formerPostdoc || []).forEach((entry) => {
            const name = String(entry).split(':')[0].trim();
            if (name && !seen.has(name)) {
                seen.add(name);
                merged.push(entry);
            }
        });
        return merged;
    };

    const personLinks = {
        "Amir Gholami": "https://amirgholami.org/",
        "Wenzhao Zheng": "https://wzzheng.net/",
        "Coleman Hooper": "https://colemanhooper.org/",
        "Nicholas Lee": "https://nicholaslee.me/",
        "Haocheng Xi": "https://haochengxi.github.io/",
        "Rishabh Tiwari": "https://www.rishabhtiwari.org/",
        "Xiuyu Li": "https://xiuyuli.com/",
        "Yuezhou Hu": "https://yuezhouhu.github.io/",
        "Harman Singh": "https://harmandotpy.github.io/",
        "Monishwaran Maheshwaran": "https://www.monishwaran.com/",
        "Lutfi Eren Erdogan": "https://www.linkedin.com/in/le-erdogan/",
        "Minseo Kim": "https://minseokim.org/",
        "Arnav Chavan": "https://www.linkedin.com/in/arnav0400/",
        "Sebastian Zhao": "https://www.linkedin.com/in/sebbyzhao/",
        "Sophia Shao": "https://people.eecs.berkeley.edu/~ysshao/",
        "Gopala Anumanchipalli": "https://www2.eecs.berkeley.edu/Faculty/Homepages/gopala.html",
        // Recent/Former postdocs
        "Ashok Sudarsanam": "https://www.linkedin.com/in/ashok-sudarsanam/",
        "Shanghang Zhang": "https://www.shanghangzhang.com/",
        "Huanrui Yang": "https://sites.google.com/view/huanrui-yang",
        "Sicheng Zhao": "https://sites.google.com/view/schzhao",
        "Dorothea Kolossa": "https://www.linkedin.com/in/dorotheakolossa/?locale=en_US",
        "Khalid Ashraf": "https://www.linkedin.com/in/khalid-ashraf-2b8a4a33/",
        "Chidamber Kulkarni": "https://www.linkedin.com/in/chidamber/?originalSubdomain=ca",
        "Alberto Todeschini": "https://www.ischool.berkeley.edu/people/alberto-todeschini",
        "Matthias Gries": "http://www.mgries.net/",
        "Serdar Tasiran": "https://www.tasiran.org/",
        "Youngmin Yi": "http://parlab.uos.ac.kr/",
        "Michael Orshansky": "https://users.ece.utexas.edu/~michael/",
        // PhD Alumni
        "Chenfeng Xu": "https://www.chenfengx.com/",
        "Sebastian Nehrdich": "https://sebastian-nehrdich.github.io/",
        "Sehoon Kim": "https://sehoonkim.org/",
        "Peter Jin": "https://www.linkedin.com/in/peterhaijin/",
        "Zhen Dong": "https://dong-zhen.com/",
        "Colorado Reed": "https://cjrd.github.io/",
        "Sheng Shen": "https://sincerass.github.io/",
        "Bichen Wu": "https://www.linkedin.com/in/bichenwu/",
        "Bichen Wu / 吴璧辰 / Wúbìchén": "https://www.linkedin.com/in/bichenwu/",
        "Matthew Moskewicz": "https://www.linkedin.com/in/matthew-moskewicz-8237b711a/",
        "Forrest Iandola": "https://www.forrestiandola.com/",
        "Michael Anderson": "https://www.linkedin.com/in/michael-anderson-996753b/",
        // Additional PhD alumni links
        "Ekaterina Gonina": "https://www.linkedin.com/in/ekaterina-katya-gonina-0b61828/",
        "David Sheffield": "https://www.linkedin.com/in/david-sheffield-573baa2/",
        "Narayanan Sundaram": "https://www.linkedin.com/in/narayanan-sundaram-94431626/",
        "Bor-Yiing Su": "https://www.linkedin.com/in/bor-yiing-su-b677a49/",
        "Bryan Catanzaro": "https://www.linkedin.com/in/bryancatanzaro/",
        "Jike Chong": "https://www.linkedin.com/in/jikechong/",
        "Mark Murphy": "https://www.linkedin.com/in/mark-murphy-9b319711a/",
        "N. R. Satish": "https://www.linkedin.com/in/nrsatish/",
        "Kaushik Ravindran": "https://www.linkedin.com/in/kaushik-ravindran-172b0b2/",
        "William Plishker": "https://www.linkedin.com/in/william-plishker-528138/",
        "Andrew Mihal": "https://www.linkedin.com/in/andrewmihal/",
        "David Chinnery": "https://www.linkedin.com/in/david-chinnery-2310323a/",
        "Scott Weber": "https://www.linkedin.com/in/scott-weber-7881493/",
        "Niraj Shah": "https://www.linkedin.com/in/niraj-shah-5736/",
        "NIraj Shah": "https://www.linkedin.com/in/niraj-shah-5736/",
        "Pinhong Chen": "https://www.linkedin.com/in/pinhong-chen-2b8871/",
        // Former Visiting
        "Kiseok Kwon": "https://www.linkedin.com/in/kiseok-kwon-003bab17/",
        "Kees Vissers": "https://www.linkedin.com/in/kees-vissers-b7baba1/",
        "Christian Sauer": "https://www.linkedin.com/in/christiansauer/",
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'home':
                return (
                    <div className="space-y-8">
                        <div className="prose max-w-none">
                            <p>I am a Professor of the Graduate School, conducting research in the Berkeley AI Research (BAIR) lab. For the last 20 years I have focused on making algorithms in the rapidly evolving areas of Machine Learning and AI more computationally efficient. Currently I am focused on the computational efficiency of  Large Language Models, Diffusion Language Models, and complex agentic systems. My approach to research is motivated by practical problems, and I always aspire to make a real-world impact with my work.</p>

                            <p className="mt-4">
                                My group is accepting applicants for post-doctoral fellows, PhD students, 5th Year Masters students, and outstanding Berkeley undergraduates.
                                <button
                                    type="button"
                                    onClick={() => setShowContactGuidance(prev => !prev)}
                                    aria-expanded={showContactGuidance}
                                    aria-controls="contact-guidance"
                                    className="ml-2 inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium underline underline-offset-4 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-sm"
                                >
                                    Read this before you contact me!
                                    <ChevronDown size={16} className={`transition-transform ${showContactGuidance ? 'rotate-180' : ''}`} />
                                </button>
                            </p>

                            {showContactGuidance && (
                                <div id="contact-guidance" className="mt-3 rounded-md border-l-4 border-yellow-400 bg-yellow-50 p-4 shadow-sm">
                                    <div className="space-y-3 text-gray-800">
                                        <p>
                                            Please be aware that admission to our PhD program is very competitive, and a track record of first-author publication at top AI venues such as NeurIPS, ICML, ICLR, and other top Computer Vision and Robotics conferences is highly recommended. Moreover, generic emails to me regarding consideration for admission are a waste of  time for both of us. Applicants will need to go through the rigorous graduate admissions process before we will have any chance of working together.  Further, if you indicate me as a faculty of interest, and have a strong admissions packet, I will be alerted to your application anyway.
                                        </p>
                                        <p>
                                            Potential post-doctoral fellows may contact me by emailing me at the campus address. Please put “Application for a Post-doctoral Fellow position” in the subject heading of your email, and include a current CV.
                                        </p>
                                        <p>
                                            Finally, unfortunately, I don't have the advising bandwidth, nor the time to handle visa details and so forth, to accommodate  undergraduate summer interns from other institutions.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <p className="mt-4">In my approach to advising PhD students and post-doctoral fellows I work hard to deeply understand each individual's personal goals and career ambitions. A faculty position, an influential position at an industrial lab, or CEO of a startup: a successful start in each of these careers will require a different emphasis during your PhD or post-doctoral fellowship, and I will work with you to ensure you are best prepared to achieve your goals.</p>

                            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                <h3 className="text-lg font-semibold mb-2">Contact Information</h3>
                                <p><strong>Address:</strong> {profileData.contact.address}</p>
                            </div>
                        </div>
                    </div>
                );

            case 'interests':
                return (
                    <div className="space-y-6">
                        <div className="prose max-w-none space-y-4">
                            <h3 className="text-xl font-semibold mb-2">Current Research</h3>
                            <p>
                                The unifying feature of my research career has been the search for computationally efficient algorithms for key real-world problems. For the last 20 years I have focused on computationally efficient algorithms and NN models in the rapidly evolving areas of Machine Learning and AI. Currently my group is focused on the computational efficiency of Large Language Models, Diffusion Language Models, and complex agentic systems, but given these rapidly evolving areas, we could be expanding our focus to a new challenge at any time!
                            </p>

                            <h3 className="text-xl font-semibold mt-6 mb-2">Prior Research</h3>
                            <p>
                                I began my research career in industry where I developed efficient algorithms for logic synthesis and related areas. This led to many awards, the most prestigious being “<a href="https://eecs.berkeley.edu/news/kurt-keutzer-receives-dac-most-influential-paper-award/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Most Influential Paper of the 1980’s</a>” from the Design Automation Conference. At the 50th Design Automation Conference in 2013 I received awards for “A Top 10 Most Cited Author,” “Author of a Top 10 Most Cited Paper,”  and  I was recognized as one of only three people to win four Best-Paper Awards in the history of that conference.  I followed my early research to Synopsys where I became their first CTO.
                            </p>
                            <p>
                                As Moore’s Law continued to exponentially increase the computing power that could be implemented on a single chip, my research evolved to consider the problems associated with efficiently implementing embedded software on “systems on a chip.”  By the year 2000 single-chip multiprocessors became a commercial reality, and my research began to focus on designing and programming application-specific multiprocessors.
                            </p>
                            <p>
                                Our focus on parallelism led to our joining the ParLab where in the paper “<a href="https://icml.cc/Conferences/2008/papers/491.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Fast support vector machine training and classification on graphics processors</a>,” my student <a href="https://www.linkedin.com/in/bryancatanzaro/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Bryan Catanzaro</a> was the first to demonstrate the potential for GPUs to accelerate machine learning algorithms.  From 2008 to 2014 my group continued to show the potential for GPUs to accelerate diverse machine learning algorithms, such as hidden-markov models, gaussian mixture models, as well as to assemble these in systems for object detection and other applications.
                            </p>
                            <p>
                                By 2013 we shifted our focus on Neural Nets, and my student <a href="https://forrestiandola.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Forrest Iandola</a> was the first to publish that the training of Convolutional Neural Nets could be significantly accelerated by scaling up to 128 GPUs. We then used this advance to design the “Squeeze” family of Neural Nets, including <a href="https://en.wikipedia.org/wiki/SqueezeNet" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">SqueezeNet</a>, <a href="https://arxiv.org/pdf/1612.01051" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">SqueezeDet</a>, <a href="https://arxiv.org/pdf/1710.07368" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">SqueezeSeg</a>, and others. This work helped to pioneer the migration of Neural Nets to edge and mobile applications. My PhD graduate, <a href="https://www.linkedin.com/in/bichenwu/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Bichen Wu</a>, was also the first to publish the potential of using transformers for vision applications in his paper "<a href="https://arxiv.org/pdf/2006.03677" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Visual transformers: Token-based image representation and processing for computer vision</a>" published just a few months ahead of the much more famous ViT paper.
                            </p>
                            <p>
                                To bring us back to the present, noticing the amazing success of Large Language Models, and with <a href="https://amirgholami.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Dr. Amir Gholami</a>’s encouragement, our group has, most recently, redirected their toolkit of techniques for algorithmic efficiency to LLMs, DLMs, and agentic systems.
                            </p>
                        </div>
                    </div>
                );

            case 'researchGroup':
                return (
                    <div className="space-y-8">
                        <h2 className="text-2xl font-bold border-b-2 border-blue-500 pb-2">Research Group</h2>

                        <div className="space-y-6">
                            <div>
                                <h3 className="text-xl font-semibold mb-2">Faculty and Postdocs</h3>
                                <ul className="list-disc pl-6 space-y-1">
                                    {profileData.researchGroup.facultyAndPostdocs.map((p, i) => (
                                        <li key={i}>
                                            {personLinks[p.name]
                                                ? <a href={personLinks[p.name]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">{p.name}</a>
                                                : p.name}
                                            {p.role ? `: ${p.role}` : ''}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-xl font-semibold mb-2">PhD Students</h3>
                                <ul className="list-disc pl-6 space-y-1">
                                    {profileData.researchGroup.phdStudents.map((p, i) => {
                                        const link = personLinks[p.name];
                                        const coMatch = p.role && p.role.match(/\(co-advised with ([^)]+)\)/i);
                                        const advisor = coMatch ? coMatch[1].trim() : null;
                                        const advisorLink = advisor ? personLinks[advisor] : null;
                                        return (
                                            <li key={i}>
                                                {link ? <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">{p.name}</a> : p.name}
                                                {advisor && (
                                                    <>
                                                        {" (co-advised with "}
                                                        {advisorLink ? (
                                                            <a href={advisorLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">{advisor}</a>
                                                        ) : (
                                                            advisor
                                                        )}
                                                        {")"}
                                                    </>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-xl font-semibold mb-2">Masters/Gap Year Students</h3>
                                <ul className="list-disc pl-6 space-y-1">
                                    {profileData.researchGroup.mastersAndGap.map((p, i) => {
                                        const link = personLinks[p.name];
                                        return (
                                            <li key={i}>
                                                {link ? <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">{p.name}</a> : p.name}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-xl font-semibold mb-2">Former Postdoctoral Fellows</h3>
                                <ul className="list-disc pl-6 space-y-1">
                                    {getMergedFormerPostdocs().map((item, i) => {
                                        const parts = String(item).split(":");
                                        const name = parts[0].trim();
                                        const rest = parts.slice(1).join(":").trim();
                                        const link = personLinks[name];
                                        return (
                                            <li key={i}>
                                                {link ? (
                                                    <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">{name}</a>
                                                ) : name}
                                                {rest ? `: ${rest}` : ''}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-xl font-semibold mb-2">PhD Alumni</h3>
                                <ul className="list-disc pl-6 space-y-1">
                                    {profileData.researchGroup.phdAlumni.map((p, i) => {
                                        const y = getGradYearForName(p.name);
                                        const link = personLinks[p.name] || personLinks[(p.name || '').split(' / ')[0]];
                                        return (
                                            <li key={i}>
                                                {link ? (
                                                    <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">{p.name}</a>
                                                ) : p.name}
                                                {y ? ` (${y})` : ''}
                                                {p.role ? `: ${p.role}` : ''}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold mb-2">Former Visiting Industrial Fellows</h3>
                                <ul className="list-disc pl-6 space-y-1">
                                    {profileData.students.formerVisiting.map((visitor, index) => {
                                        const text = String(visitor);
                                        const [rawName, ...restParts] = text.split(",");
                                        const name = (rawName || "").trim();
                                        const rest = restParts.join(",").trim();
                                        const link = personLinks[name];
                                        return (
                                            <li key={index}>
                                                {link ? (
                                                    <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">{name}</a>
                                                ) : name}
                                                {rest ? `, ${rest}` : ""}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>

                        {/* Legacy section removed as requested */}
                    </div>
                );

            case 'publications':
                return (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold border-b-2 border-blue-500 pb-2">Publications</h2>
                        <div className="prose max-w-none space-y-4">
                            <p>
                                Our research group is very productive. Through work centered in our group, as well as through Berkeley’s very open and collaborative atmosphere, in an average year we will publish in NeurIPS, ICML, ICLR, computer vision venues such as CVPR, ICCV, and ECCV, and NLP-oriented venues such as ACL, EMNLP, and COLM.
                            </p>
                            <p>
                                The best way to become familiar with the group’s research is simply to view my <a href="https://scholar.google.com/citations?hl=en&user=ID9QePIAAAAJ&view_op=list_works&sortby=pubdate" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Google Scholar Page</a>.
                            </p>
                        </div>
                    </div>
                );

            case 'aiForGood':
                return (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold border-b-2 border-blue-500 pb-2">AI for Good</h2>
                        <div className="prose max-w-none space-y-4">
                            <p>
                                AI is such a powerful technology that I think it’s especially important to align its usage with positive societal impact. In line with my pragmatic attitude, I’m very happy to have a couple opportunities to move beyond talking about “AI for Good” to actually achieve that aspiration. Here are a couple of my favorite projects.
                            </p>

                            <h3 className="text-lg font-semibold">Every Person Deserves a Personal Tutor</h3>
                            <p>
                                <a href="https://sigiq.ai/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">SigIQ.ai</a> is a startup co-founded by Berkeley PhD <a href="https://www.linkedin.com/in/karttikeya-mangalam-ph-d-9248a6110/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Karttikeya Mangalam</a> and me. While a for-profit corporation, it is rooted in the altruistic aspiration to advance education, particularly for the underprivileged, by  giving  each person a personal tutor. I can’t imagine a better application of this than giving under resourced children a personal tutor,  as we are doing in collaboration with <a href="https://www.linkedin.com/in/adam-stich-a2419b41/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Adam Stich</a> at the <a href="https://greaterdaytonschool.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">The Greater Dayton School</a>. To get a sense of what we’re up to there, take a look at this video: AI Tutor at the Greater Dayton School. Obviously, this is just the beginning!
                            </p>

                            <div className="mt-2">
                                <video
                                    className="w-full rounded-md shadow"
                                    controls
                                    src={gdsVideo}
                                >
                                    Your browser does not support the video tag.
                                </video>
                            </div>

                            <h3 className="text-lg font-semibold">Fostering the Study of Tibetan, Sanskrit, and the World’s Wisdom Traditions</h3>
                            <p>
                                I’ve been a lifelong student of the world’s wisdom traditions, and for me this has required a study of largely neglected languages such as Tibetan and Sanskrit. I have been very happy to work with <a href="https://www.linkedin.com/in/sebastian-nehrdich-349773248/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Sebastian Nehrdich</a> to provide tools to better explore the literature of these languages, and to provide machine translation for them, through <a href="http://dharmamitra.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">dharmamitra.org</a>.
                            </p>
                        </div>
                    </div>
                );

            case 'entrepreneurship':
                return (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold border-b-2 border-blue-500 pb-2">Entrepreneurship</h2>
                        <div className="prose max-w-none space-y-4">
                            <h3 className="text-xl font-semibold mb-2">Our Current Entrepreneurial Environment</h3>
                            <p>
                                Berkeley is enjoying something of an entrepreneurial golden age. Entrepreneurial efforts have clear and strong support from our Chancellor <a href="https://www.linkedin.com/in/rich-lyons-30332b1/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Richard Lyons</a> down through all levels of the university. I welcome entrepreneurially oriented students and post-docs, and I do my best to mentor them, drawing on my 30+ years of experience with over 35 startups as a co-founder (<a href="https://en.wikipedia.org/wiki/DeepScale" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">DeepScale</a>, <a href="http://Narada.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Narada.ai</a>, <a href="http://Nexusflow.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Nexusflow.ai</a>, and <a href="http://SigIQ.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">SigIQ.ai</a>), angel investor, and/or advisor.
                            </p>

                            <h3 className="text-xl font-semibold mb-2">A Bit of History</h3>
                            <p>
                                After leaving AT&amp;T Bell Labs Research I joined a startup, Synopsys, Inc. where I had an ideal startup journey. I began at Synopsys as an individual contributor in a small research group and grew with the company to become CTO and SVP of a public company, running a research group of 35 PhDs. While CTO, Synopsys became a technology leader in Electronic Design Automation, and I saw for myself how fast-moving startups can be an epicenter of innovation competing successfully with universities and larger industrial research labs. There’s a fun interview with me about those years <a href="https://open.spotify.com/episode/0mCbMS4Zj5OWRuvoJ6wcOj?si=jkIxCT8pRKu9ObNyz-SgKQ" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">here</a>.
                            </p>
                            <p>
                                I brought this entrepreneurial spirit to Berkeley; however, when I first joined Berkeley the climate for startups and faculty was not quite as hospitable as it is today. So for many years I principally participated in startups as an investor and advisor. Some of my most successful early investments were Coverity (acquired by Synopsys), where I was the first investor, Tensilica (acquired by Cadence) where I was in the group of first investors, and Right-Track-CAD (acquired by Alterra), where I was the sole external investor. I was also fortunate to advise two companies that went on to successful IPOs: C-Cube Microsystems and Simplex.
                            </p>

                            <h3 className="text-xl font-semibold mb-2">More Recently</h3>
                            <p>
                                As my research focus evolved, and campus attitudes toward startups warmed up, so did my entrepreneurial activities. I co-founded DeepScale in 2015 and we were acquired by Tesla in 2019. I was also in the group of first investors at Pieter Abbeel’s Covariant (reverse acquired by Amazon), Chris Rowen’s BabbleLabs (acquired by Cisco), and an early investor in Song Han’s OmniML (acquired by NVIDIA). Most recently I co-founded <a href="http://Nexusflow.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Nexusflow.ai</a> (acquired by NVIDIA). I am currently a co-founder of <a href="http://Narada.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Narada.ai</a> and <a href="http://SigIQ.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">SigIQ.ai</a>.
                            </p>
                            <p>
                                My current investor/advisor relationships include <a href="https://www.graphistry.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Graphistry</a>, <a href="https://www.promptai.co/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Prompt.ai</a>, <a href="http://Ventrilo.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Ventrilo.ai</a>, and <a href="https://xmentium.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Xmentium</a>. Among my current active investments are <a href="http://Eigen.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Eigen.ai</a>, <a href="http://Eventual.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Eventual.ai</a>, and <a href="http://Perplexity.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Perplexity.ai</a>. I also currently advise <a href="https://latentai.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Latent.ai</a>, <a href="https://shorelineai.us/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Shoreline.ai</a>, and <a href="http://Sima.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Sima.ai</a>.
                            </p>
                            <p>
                                I am also engaged in various ways with the venture capital community. In particular, I am an <a href="https://www.thehouse.fund/team" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">AI Faculty Partner</a> at <a href="https://www.thehouse.fund/team" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">The House Fund</a>, and Limited Partner at <a href="https://www.aixventures.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">AIX Ventures</a> and <a href="https://laude.vc/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Laude Ventures</a>.
                            </p>

                            <h3 className="text-xl font-semibold mb-2">Education</h3>
                            <p>
                                I believe strongly that entrepreneurial skills can be taught and I object to simplistic notions that “it’s all a matter of luck” or “only 1 in 10 startups are successful.” I’m very pleased to have twice taught the classic Lean Launchpad course with its originator <a href="https://steveblank.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Steve Blank</a>. I have taught a variety of other entrepreneurship courses in both the School of Engineering and the Haas Business School at Berkeley.
                            </p>
                            <p>
                                Generally speaking, the entrepreneurial environment is always evolving and I’m happily challenged to be evolving with it.
                            </p>
                        </div>
                    </div>
                );

            default:
                return <div>Section not found</div>;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-600 text-white p-8 rounded-b-3xl shadow-lg">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-1">
                        <h1 className="text-4xl md:text-5xl font-bold mb-2">{profileData.name}</h1>
                        <p className="text-xl mb-4">{profileData.title}</p>
                        <div className="flex flex-wrap gap-3 mt-4">
                            <a href="mailto:keutzer@berkeley.edu" className="bg-white text-blue-900 px-4 py-2 rounded-full flex items-center gap-2 hover:bg-blue-50 transition-colors">
                                <Mail size={16}/> Email
                            </a>
                            <a href="https://scholar.google.com/citations?user=ID9QePIAAAAJ&hl=en" className="bg-white text-blue-900 px-4 py-2 rounded-full flex items-center gap-2 hover:bg-blue-50 transition-colors" target="_blank" rel="noopener noreferrer">
                                <BookOpen size={16}/> Google Scholar
                            </a>
                            <a href="https://www.linkedin.com/in/kurtkeutzer/" className="bg-white text-blue-900 px-4 py-2 rounded-full flex items-center gap-2 hover:bg-blue-50 transition-colors" target="_blank" rel="noopener noreferrer">
                                <Linkedin size={16}/> LinkedIn
                            </a>
                        </div>
                    </div>
                    <div className="w-40 h-40 md:w-60 md:h-60 rounded-full overflow-hidden shadow-lg">
                        <img
                            src={profileData.image}
                            alt={profileData.name}
                            className="w-full h-full object-cover object-[center_4%]"
                        />
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row gap-8">
                {/* Sidebar Navigation */}
                <div className="md:w-64 bg-white rounded-lg shadow-md p-6 sticky top-8">
                    <h2 className="text-xl font-semibold mb-6">Navigation</h2>
                    <nav className="space-y-2">
                        {sections.map((section) => (
                            section.externalUrl ? (
                                <a
                                    key={section.id}
                                    href={section.externalUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors hover:bg-gray-100 text-gray-700"
                                >
                                    <section.icon size={18}/>
                                    {section.title}
                                </a>
                            ) : (
                                <button
                                    key={section.id}
                                    onClick={() => setActiveSection(section.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                                        activeSection === section.id
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'hover:bg-gray-100 text-gray-700'
                                    }`}
                                >
                                    <section.icon size={18}/>
                                    {section.title}
                                </button>
                            )
                        ))}
                    </nav>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 bg-white rounded-lg shadow-md p-6">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default App;